import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatINR, PRODUCTS } from "@/lib/products";
import { Minus, Plus, X, ChevronRight, Gift, ShieldCheck, Truck } from "lucide-react";
import { ProductCard } from "@/components/product/ProductCard";
import { useState } from "react";

export default function Cart() {
  const { items, updateQty, removeItem, clear, subtotal, shipping, tax, total, shippingThreshold } = useCart();
  const [coupon, setCoupon] = useState("");
  const [giftWrap, setGiftWrap] = useState(false);
  const [giftNote, setGiftNote] = useState("");

  const progress = Math.min(100, (subtotal / shippingThreshold) * 100);
  const remaining = Math.max(0, shippingThreshold - subtotal);
  const recommended = PRODUCTS.filter((p) => p.category === "accessories").slice(0, 4);

  return (
    <div data-testid="cart-page">
      <div className="container-nc pt-10">
        <div className="flex items-center gap-2 text-xs text-muted_brown mb-8">
          <Link to="/" className="hover:text-espresso">Home</Link>
          <ChevronRight size={12}/>
          <span className="text-espresso">Cart</span>
        </div>

        <h1 className="font-serif text-5xl md:text-6xl text-espresso mb-3">Your Cart</h1>
        <p className="text-muted_brown">
          {items.length === 0 ? "Your cart is currently empty." : `${items.length} item${items.length>1?'s':''} · Ready to checkout.`}
        </p>

        {items.length > 0 && (
          <>
            {/* Free shipping progress */}
            <div className="mt-8 bg-ivory border border-line rounded-xl p-5">
              <div className="text-sm text-espresso">
                {remaining > 0 ? (
                  <>Add <span className="font-medium">{formatINR(remaining)}</span> more for free shipping</>
                ) : (
                  <span className="font-medium text-gold">✓ You've unlocked free shipping</span>
                )}
              </div>
              <div className="h-1.5 mt-3 bg-cream rounded-full overflow-hidden">
                <div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 mt-10">
              {/* Items */}
              <div>
                <div className="grid grid-cols-[80px_1fr] sm:grid-cols-[120px_1fr_auto] gap-4 pb-3 border-b border-line text-xs uppercase tracking-[0.2em] text-muted_brown">
                  <div className="col-span-2 sm:col-span-1">Product</div>
                  <div className="hidden sm:block">Details</div>
                  <div className="hidden sm:block text-right">Total</div>
                </div>

                {items.map((i) => (
                  <div key={i.key} data-testid={`cart-row-${i.slug}`} className="grid grid-cols-[80px_1fr] sm:grid-cols-[120px_1fr_auto] gap-4 py-6 border-b border-line items-start">
                    <img src={i.image} alt={i.name} className="w-full aspect-square object-cover rounded-lg"/>
                    <div className="min-w-0">
                      <Link to={`/product/${i.slug}`} className="font-serif text-lg text-espresso hover:underline">
                        {i.name}
                      </Link>
                      <div className="text-xs text-muted_brown mt-1">
                        {[i.size, i.cover, i.personalization, i.pageColor, i.color].filter(Boolean).join(" - ")}
                      </div>
                      <div className="mt-3 flex items-center gap-4 flex-wrap">
                        <div className="flex items-center border border-line rounded-full">
                          <button onClick={() => updateQty(i.key, i.qty - 1)} data-testid={`dec-${i.slug}`} className="w-8 h-8 flex items-center justify-center hover:bg-ivory rounded-l-full">
                            <Minus size={12}/>
                          </button>
                          <span className="w-8 text-center text-sm">{i.qty}</span>
                          <button onClick={() => updateQty(i.key, i.qty + 1)} data-testid={`inc-${i.slug}`} className="w-8 h-8 flex items-center justify-center hover:bg-ivory rounded-r-full">
                            <Plus size={12}/>
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(i.key)}
                          data-testid={`remove-${i.slug}`}
                          className="text-xs text-muted_brown hover:text-espresso flex items-center gap-1"
                        >
                          <X size={12}/> Remove
                        </button>
                      </div>
                      <div className="text-xs text-gold mt-2">In stock</div>
                    </div>
                    <div className="text-espresso font-medium col-span-2 sm:col-span-1 sm:text-right">
                      {formatINR(i.priceINR * i.qty)}
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-between mt-6">
                  <Link to="/shop" className="text-sm text-espresso hover:underline">← Continue Shopping</Link>
                  <button onClick={clear} className="text-sm text-muted_brown hover:text-espresso" data-testid="clear-cart">
                    Clear Cart
                  </button>
                </div>

                {/* Gift */}
                <div className="mt-10 bg-ivory border border-line rounded-xl p-6">
                  <div className="flex items-center gap-2 text-espresso mb-3">
                    <Gift size={16}/>
                    <span className="text-sm font-medium">Add a gift note</span>
                  </div>
                  <textarea
                    value={giftNote}
                    onChange={(e) => setGiftNote(e.target.value)}
                    placeholder="Write a personal note (optional)"
                    data-testid="gift-note"
                    className="input-nc bg-cream min-h-[80px]"
                  />
                  <label className="flex items-center gap-2 mt-3 text-sm text-espresso cursor-pointer">
                    <input type="checkbox" checked={giftWrap} onChange={(e) => setGiftWrap(e.target.checked)} className="accent-espresso" data-testid="gift-wrap"/>
                    Add gift wrap (+₹99)
                  </label>
                </div>
              </div>

              {/* Summary */}
              <aside className="lg:sticky lg:top-24 lg:self-start">
                <div className="bg-ivory border border-line rounded-xl p-6">
                  <h3 className="font-serif text-2xl text-espresso mb-5">Order Summary</h3>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between text-muted_brown">
                      <dt>Subtotal</dt>
                      <dd className="text-espresso">{formatINR(subtotal)}</dd>
                    </div>
                    <div className="flex justify-between text-muted_brown">
                      <dt>Shipping</dt>
                      <dd className="text-espresso">{shipping === 0 ? "Free" : formatINR(shipping)}</dd>
                    </div>
                    <div className="flex justify-between text-muted_brown">
                      <dt>Estimated Tax</dt>
                      <dd className="text-espresso">{formatINR(tax)}</dd>
                    </div>
                    {giftWrap && (
                      <div className="flex justify-between text-muted_brown">
                        <dt>Gift wrap</dt>
                        <dd className="text-espresso">₹99</dd>
                      </div>
                    )}
                  </dl>
                  <div className="border-t border-line my-5" />
                  <div className="flex items-baseline justify-between">
                    <span className="font-serif text-xl text-espresso">Total</span>
                    <span className="font-serif text-2xl text-espresso" data-testid="cart-total">
                      {formatINR(total + (giftWrap ? 99 : 0))}
                    </span>
                  </div>

                  <div className="mt-5 flex gap-2">
                    <input
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value)}
                      placeholder="Promo code"
                      data-testid="coupon-input"
                      className="input-nc bg-cream text-sm"
                    />
                    <button className="btn-outline px-5 py-2 text-xs" data-testid="apply-coupon">Apply</button>
                  </div>

                  <Link to="/checkout" data-testid="proceed-checkout" className="btn-primary w-full mt-5">
                    Proceed to Checkout
                  </Link>

                  <div className="mt-5 space-y-2 text-xs text-muted_brown">
                    <div className="flex items-center gap-2"><Truck size={12}/> Free shipping over ₹999</div>
                    <div className="flex items-center gap-2"><ShieldCheck size={12}/> Secure encrypted checkout</div>
                  </div>
                </div>
              </aside>
            </div>

            {/* Recommended */}
            <section className="mt-24 pb-8">
              <h2 className="font-serif text-3xl text-espresso mb-8">Complete your kit</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {recommended.map((p) => <ProductCard key={p.id} product={p}/>)}
              </div>
            </section>
          </>
        )}

        {items.length === 0 && (
          <div className="py-16">
            <Link to="/shop" className="btn-primary">Start Shopping</Link>
          </div>
        )}
      </div>
    </div>
  );
}
